import Vue from 'vue';
import outgoingPostMessageUtils from '@/utils/eventCommunicationUtils/outgoingPostMessageUtils';
import pendoService from '@/modules/externalScripts/services/pendoService';
import store from '@/store';

const isAnalyticsDisabled = () => store.getters['BusinessStore/isAnalyticsDisabled'];

const AnalyticsPlugin = {
  // eslint-disable-next-line no-unused-vars
  install(app, vueOptions) {
    // eslint-disable-next-line no-param-reassign
    app.prototype.$track = function track(eventName, options = {}) {
      const povAnalyticsEnabled = Vue.prototype.$hasFeature?.('pov_analytics_enabled');
      
      if (!povAnalyticsEnabled) {
        // Feature disabled: Send to Angular (legacy behavior)
        outgoingPostMessageUtils.post('povAnalyticsEvent', {
          eventName,
          options,
        });
        return;
      }
      
      if (isAnalyticsDisabled()) {
        return;
      }
      
      if (window.mixpanel) {
        window.mixpanel.track(eventName, {
          ...options,
          source: 'pov',
        });
      }
      
      if (window.ga && typeof window.ga === 'function') {
        window.ga('send', {
          hitType: 'event',
          eventCategory: options.category || 'User Action',
          eventAction: eventName,
          eventLabel: options.label || '',
        });
      }
      
      if (window.pendo?.track && !pendoService.isPendoDenied()) {
        window.pendo.track(eventName, options);
      }
    };

    /*
     * INTERNAL: $trackMixpanel - Only sends to Mixpanel (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackMixpanel = function trackMixpanel(eventName, options = {}) {
      if (!isAnalyticsDisabled() && window.mixpanel) {
        window.mixpanel.track(eventName, options);
      }
    };

    /*
     * INTERNAL: $trackGA - Only sends to Google Analytics (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackGA = function trackGA(eventName, options = {}) {
      if (!isAnalyticsDisabled() && window.ga) {
        window.ga('send', {
          hitType: 'event',
          eventCategory: options.category || 'User Action',
          eventAction: eventName,
          eventLabel: options.label || '',
        });
      }
    };

    /*
     * INTERNAL: $trackPendo - Only sends to Pendo (no postMessage)
     * Used when receiving events FROM iframes (Angular/Vue)
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$trackPendo = function trackPendoInternal(eventName, options = {}) {
      if (!isAnalyticsDisabled() && window.pendo?.track && !pendoService.isPendoDenied()) {
        window.pendo.track(eventName, options);
      }
    };

    /**
     * Set username/user ID for analytics
     */
    // eslint-disable-next-line no-param-reassign
    app.prototype.$setUsername = function setUsername(username) {
      if (isAnalyticsDisabled()) return;
      
      if (window.mixpanel) {
        window.mixpanel.identify(username);
      }
      
      if (window.ga && typeof window.ga === 'function') {
        window.ga('set', 'userId', username);
      }
    };

    // eslint-disable-next-line no-param-reassign
    app.prototype.$setUserProperties = function setUserProperties(properties = {}) {
      if (isAnalyticsDisabled()) return;
      if (window.mixpanel && window.mixpanel.people) {
        window.mixpanel.people.set(properties);
      }
      
      // (GA doesn't have a direct equivalent to Mixpanel's people properties)
    };
    
    window.addEventListener('message', (event) => {
      // IMPORTANT: Mirrors behavior from vue/src/plugins/analytics.js
      // If POV is hosting a micro-service iframe - get analytics events
      // Consider any modifications made here to be included in the Vue equivalent
      const eventData = event.data;
      
      // Handle analytics events from iframes (Angular/Vuetage)
      if (eventData.event === 'analyticsServiceEvent') {
        const data = eventData.data;
        
        // Handle different types of analytics calls
        switch (data.method) {
          case 'setUserProperties':
            app.prototype.$setUserProperties(data.properties);
            break;
            
          case 'setUsername':
            app.prototype.$setUsername(data.username);
            break;
            
          default:
            if (data.eventName) {
              if (eventData.origin) data.options.source = eventData.origin;
              app.prototype.$track(data.eventName, data.options);
            }
            break;
        }
      }
    });
  },
};

Vue.use(AnalyticsPlugin);

export default AnalyticsPlugin;
